import { Box, Text, Tooltip, VStack, Spacer } from "@chakra-ui/react"
import { User } from "container/user"
import { Vesting } from "container/reward/Vesting"
import Big from "big.js"
import React, { useCallback, useEffect, useState } from "react"
import { MotionHStack, MotionVStack } from "./"
import { bigNum2Big, formatNumber } from "util/format"
import { getExchangeRate } from "api"
import { Contract, CONTRACT_ADDRESS } from "../../../container/contract"
import { useContractEvent, useToken } from "../../../hook/useToken"
import { BIG_ZERO } from "../../../constant/number"

export function PoolInfo() {
    const {
        state: { address },
    } = User.useContainer()
    const { StakePerpToken } = Contract.useContainer()
    const { balance: stakingBalance } = useToken(CONTRACT_ADDRESS.STAKE_PERP_TOKEN, 18)
    const [totalSupply, setTotalSupply] = useState(BIG_ZERO)
    const poolShareRate = totalSupply.eq(0) ? 0 : stakingBalance.div(totalSupply).mul(100).toFixed(2)

    const [poolSize, setPoolSize] = useState<Big | undefined>(undefined)
    const [APR, setAPR] = useState<Big | undefined>(undefined)
    const {
        state: { snapshot },
    } = Vesting.useContainer()

    const updateTotalSupply = useCallback(async () => {
        if (StakePerpToken) {
            const totalSupply = await StakePerpToken.totalSupply()
            setTotalSupply(bigNum2Big(totalSupply, 18))
        }
    }, [StakePerpToken])

    useEffect(() => {
        updateTotalSupply()
    }, [updateTotalSupply])
    useContractEvent(StakePerpToken, "Staked", updateTotalSupply)
    useContractEvent(StakePerpToken, "Unstaked", updateTotalSupply)

    useEffect(() => {
        // As both the staked and the rewarded tokens are PERP, we don't need to take the exchangeRate into APR calculation
        function calculateAPR(totalPerpInPool: Big): Big {
            if (totalPerpInPool.eq(0)) {
                return totalPerpInPool
            }

            // APR(USD) = (weeklyReward / totalPerpInPool) * 52
            let weeklyPerpReward = new Big(0)
            Object.values(snapshot).forEach(item => {
                const weeks = Object.values(item)
                const lastWeek = weeks[weeks.length - 1]
                weeklyPerpReward = weeklyPerpReward.add(lastWeek.totalRewardPerp)
            })

            // Set the default weeklyPerpReward = 150,000
            if (weeklyPerpReward.eq(0)) {
                weeklyPerpReward = new Big(150000)
            }

            return weeklyPerpReward.div(totalPerpInPool).mul(52).mul(100)
        }
        async function calculate() {
            const total = totalSupply
            const res = await getExchangeRate("perpetual-protocol", "usd")
            const resJson = await res.json()
            const exchangeRate = resJson["perpetual-protocol"].usd
            const poolSize = total.mul(exchangeRate)
            setPoolSize(poolSize)
            setAPR(calculateAPR(total))
        }
        calculate()
    }, [snapshot, totalSupply])

    return (
        <MotionVStack
            w="100%"
            float="right"
            fontWeight="bold"
            bgColor="#E8EADB"
            borderRadius="2xl"
            borderBottomRadius="60px"
            py={5}
            px={7}
            pt={3}
            boxShadow="0px 2px 8px rgba(26, 56, 40, 0.1) inset"
        >
            {address && (
                <MotionHStack>
                    <Text color="#999" fontSize="xs">
                        MY POOL SHARE {poolShareRate} %
                    </Text>
                </MotionHStack>
            )}
            <MotionHStack>
                <VStack flexGrow={1} spacing={0} align="flex-end">
                    <Text color="#91C228" fontSize="xs">
                        EST. APR
                    </Text>
                    <Text fontSize="lg" color="#353535">
                        0
                        <Text w="100%" align="right" fontSize="11px" opacity={0.4}>
                            %
                        </Text>
                    </Text>
                </VStack>
                <Spacer />
                <Box h="60px" w="1px" bgColor="#345141" opacity={0.15} />

                <Spacer />
                <Tooltip label={poolSize ? `${formatNumber(poolSize.toFixed(2))} USD` : "--,---,---"}>
                    <VStack flexGrow={1} spacing={0} align="flex-start">
                        <Text color="#91C228" fontSize="xs">
                            POOL SIZE
                        </Text>
                        <Text fontSize="lg" color="#353535">
                            {totalSupply ? formatNumber(totalSupply.toFixed(2)) : "--,---,---"}
                            <Text fontSize="11px" opacity={0.4}>
                                PERP
                            </Text>
                        </Text>
                    </VStack>
                </Tooltip>
            </MotionHStack>
        </MotionVStack>
    )
}
